import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal, untracked } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { TagModule } from 'primeng/tag';
import { DrawerModule } from 'primeng/drawer';
import { ToastrService } from 'ngx-toastr';
import { AuthService } from '../../services/auth.service';
import { CustomerService, type CustomerDTO } from '../../services/customer.service';
import type { CollectionAttemptEventDTO, MitLimits, MitTransactionDTO } from '../../core/models';
import { CollectionAttemptTimelineComponent } from '../collection-attempts/collection-attempt-timeline.component';
import {
  MIT_ACTOR_LABELS,
  MIT_LIMIT_ROWS,
  MIT_TRANSACTION_STATUS_LABELS,
  MIT_TRANSACTION_TYPE_LABELS,
  MitLimitRow,
  MitService,
  mitApiError,
  mitLimitValue,
  mitRejectionLabel,
  mitTransactionStatusSeverity
} from '../../services/mit.service';
import { MitTransactionResolveDialogComponent } from './mit-transaction-resolve-dialog.component';
import { CustomerReadComponent } from '../customers/customer-read.component';
import { MitComercialMetadataViewComponent } from '../mit-mandates/mit-comercial-metadata-view.component';

interface EvaluationUsageRow {
  label: string;
  value: string;
}

@Component({
  selector: 'app-mit-transaction-read',
  templateUrl: './mit-transaction-read.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  styles: [
    `
      /* appendTo="body" saca el drawer del host: no usar :host ::ng-deep */
      ::ng-deep .mit-tx-customer-drawer .p-drawer-header {
        background: rgb(241 245 249);
        border-bottom: 1px solid rgb(226 232 240);
        box-shadow: 0 1px 3px 0 rgb(15 23 42 / 0.06);
        padding: 1rem 1.25rem;
      }
      ::ng-deep .mit-tx-customer-drawer .p-drawer-content {
        background: rgb(248 250 252);
        padding: 1rem 1.25rem;
      }
    `
  ],
  imports: [CommonModule, RouterLink, ButtonModule, TagModule, DrawerModule, MitTransactionResolveDialogComponent, CustomerReadComponent,
    MitComercialMetadataViewComponent, CollectionAttemptTimelineComponent]
})
export class MitTransactionReadComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private auth = inject(AuthService);
  private mitService = inject(MitService);
  private customerService = inject(CustomerService);

  private toastr = inject(ToastrService);

  /** Cuando viene del listado, carga este id y oculta la navegación de página. */
  transactionId = input<number | null>(null);
  embedded = input(false);
  changed = output<MitTransactionDTO>();

  transaction = signal<MitTransactionDTO | null>(null);
  loading = signal(true);
  events = signal<CollectionAttemptEventDTO[]>([]);
  eventsLoading = signal(false);
  private eventsSeq = 0;
  private requestSeq = 0;
  notFound = signal(false);
  resolveOpen = signal(false);
  voiding = signal(false);
  selectedCustomer = signal<CustomerDTO | null>(null);
  isCustomerDetailsOpen = signal(false);
  customerDetailsLoading = signal(false);

  readonly canResolve = this.auth.hasRole('MIT_TRANSACTION_RESOLVE');
  readonly canReadMandate = this.auth.hasRole('MIT_MANDATE_READ');
  readonly limitRows = MIT_LIMIT_ROWS.filter((row) => row.key !== 'maxConsecutiveDeclines');

  voidOpen = computed(() => {
    const status = this.transaction()?.status;
    return status === 'VOID_PENDING' || status === 'VOID_FAILED';
  });
  showResolve = computed(() => this.canResolve && (this.transaction()?.status === 'PENDING_RECONCILIATION' || this.voidOpen()));
  showRetryVoid = computed(() => this.canResolve && this.voidOpen());
  evaluation = computed(() => {
    const raw = this.transaction()?.limitsEvaluation as Record<string, unknown> | undefined;
    if (!raw) return null;
    return {
      evaluatedAt: raw['evaluatedAt'] as string | undefined,
      result: raw['result'] as string | undefined,
      limits: (raw['effectiveLimits'] ?? null) as MitLimits | null,
      usage: (raw['usage'] ?? null) as Record<string, number | string | null> | null
    };
  });
  usageRows = computed<EvaluationUsageRow[]>(() => {
    const usage = this.evaluation()?.usage;
    if (!usage) return [];
    const money = (value: unknown) => new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(Number(value ?? 0));
    return [
      { label: 'Monto del día', value: money(usage['dayAmount']) },
      { label: 'Cobros del día', value: String(usage['dayCount'] ?? 0) },
      { label: 'Monto del mes', value: money(usage['monthAmount']) },
      { label: 'Cobros del mes', value: String(usage['monthCount'] ?? 0) },
      { label: 'Monto acumulado', value: money(usage['totalAmount']) },
      { label: 'Cobros en total', value: String(usage['totalCount'] ?? 0) }
    ];
  });

  constructor() {
    effect(() => {
      const fromInput = this.transactionId();
      if (fromInput != null) {
        untracked(() => this.load(fromInput));
        return;
      }
      if (this.embedded()) return;
      const id = Number(this.route.snapshot.paramMap.get('id'));
      if (!id) {
        this.loading.set(false);
        this.notFound.set(true);
        return;
      }
      untracked(() => this.load(id));
    });
  }

  load(id: number): void {
    const seq = ++this.requestSeq;
    this.loading.set(true);
    this.notFound.set(false);
    if (this.transaction()?.id !== id) {
      this.transaction.set(null);
      this.events.set([]);
      this.closeCustomerDetails();
    }
    this.loadEvents(id);
    this.mitService.getTransaction(id).subscribe({
      next: (response) => {
        if (seq !== this.requestSeq) return;
        this.loading.set(false);
        this.transaction.set(response.body ?? null);
        this.notFound.set(!response.body);
      },
      error: () => {
        if (seq !== this.requestSeq) return;
        this.loading.set(false);
        this.transaction.set(null);
        this.notFound.set(true);
      }
    });
  }

  private loadEvents(id: number): void {
    const seq = ++this.eventsSeq;
    this.eventsLoading.set(true);
    this.mitService.getTransactionEvents(id).subscribe({
      next: (response) => {
        if (seq !== this.eventsSeq) return;
        this.eventsLoading.set(false);
        this.events.set(response.body ?? []);
      },
      error: () => {
        if (seq !== this.eventsSeq) return;
        this.eventsLoading.set(false);
        this.events.set([]);
      }
    });
  }

  openCustomerDetails(customerId: number | undefined): void {
    if (!customerId) return;
    this.isCustomerDetailsOpen.set(true);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(true);
    this.customerService.getCustomerById(customerId).subscribe({
      next: (response) => {
        if (response.ok && response.body) {
          this.selectedCustomer.set(response.body);
        } else {
          this.toastr.error(response.message || 'No se pudo cargar el cliente');
          this.closeCustomerDetails();
        }
        this.customerDetailsLoading.set(false);
      },
      error: () => {
        this.customerDetailsLoading.set(false);
        this.toastr.error('Error al cargar el cliente');
        this.closeCustomerDetails();
      }
    });
  }

  closeCustomerDetails(): void {
    this.isCustomerDetailsOpen.set(false);
    this.selectedCustomer.set(null);
    this.customerDetailsLoading.set(false);
  }

  onCustomerDrawerVisibleChange(visible: boolean): void {
    if (!visible) this.closeCustomerDetails();
  }

  editCustomerFromModal(): void {
    const customer = this.selectedCustomer();
    if (!customer?.id) return;
    this.closeCustomerDetails();
    void this.router.navigate(['/customers', customer.id]);
  }

  openSaleFromCustomer(saleId: number): void {
    if (!saleId) return;
    this.closeCustomerDetails();
    void this.router.navigate(['/sales', saleId]);
  }

  onResolved(tx: MitTransactionDTO): void {
    this.transaction.set(tx);
    this.changed.emit(tx);
    this.loadEvents(tx.id);
  }

  /** Plataforma: reintenta ahora la cancelación; si falla, siguen los reintentos automáticos. */
  retryVoid(): void {
    const tx = this.transaction();
    if (!tx || this.voiding()) return;
    this.voiding.set(true);
    this.mitService.voidTransaction(tx.id).subscribe({
      next: (response) => {
        this.voiding.set(false);
        if (response.body) {
          this.transaction.set(response.body);
          this.changed.emit(response.body);
        }
        this.loadEvents(tx.id);
        if (response.body?.status === 'VOIDED') {
          this.toastr.success('Cargo de validación cancelado');
        } else {
          this.toastr.warning('La cancelación sigue pendiente; se reintentará automáticamente');
        }
      },
      error: (err) => {
        this.voiding.set(false);
        this.toastr.error(mitApiError(err, 'No se pudo solicitar la cancelación').message);
      }
    });
  }

  statusLabel(): string {
    const status = this.transaction()?.status;
    return status ? MIT_TRANSACTION_STATUS_LABELS[status] : '—';
  }

  typeLabel(): string {
    const type = this.transaction()?.type;
    return type ? MIT_TRANSACTION_TYPE_LABELS[type] : '—';
  }

  requestedBy(): string {
    const tx = this.transaction();
    if (!tx) return '—';
    if (tx.requestedByType === 'API') return `Integración API${tx.requestedByName ? ` (${tx.requestedByName})` : ''}`;
    if (tx.type !== 'MIT_CHARGE' && !tx.requestedByUserId) return 'El cliente, al registrar su tarjeta';
    return tx.requestedByName || (tx.requestedByType ? MIT_ACTOR_LABELS[tx.requestedByType] : '—');
  }

  rejection(): string {
    return mitRejectionLabel(this.transaction()?.rejectionCode);
  }

  evaluationResult(): string {
    const result = this.evaluation()?.result;
    if (!result) return '—';
    return result === 'OK' ? 'Dentro de los límites' : mitRejectionLabel(result);
  }

  limitValue(row: MitLimitRow, limits: MitLimits | null | undefined): string {
    return mitLimitValue(row, limits);
  }

  statusSeverity = mitTransactionStatusSeverity;
}
